import React, { useState, useEffect, useCallback } from "react";
import {
  TrendingUp, Sparkles, PencilLine, AlertTriangle, Check, X,
  Filter, ShieldCheck, FileText, ArrowRight,
} from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input, Select, Modal, EmptyState } from "../lib/ui";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export const rp = (v) => (v == null ? "-" : "Rp " + Math.round(Number(v)).toLocaleString("id-ID"));
const pctStr = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${Number(v).toLocaleString("id-ID", { maximumFractionDigits: 1 })}%`);

const LEVEL_TONE = { CRITICAL: "danger", HIGH: "warn", NORMAL: "info" };
const STATUS_TONE = { PENDING_APPROVAL: "warn", APPROVED: "success", REJECTED: "danger" };
const STATUS_LABEL = { PENDING_APPROVAL: "Menunggu Persetujuan", APPROVED: "Disetujui", REJECTED: "Ditolak" };

// ---------------- Impact preview ----------------
function ImpactPreview({ data }) {
  const { t } = useT();
  if (!data) return null;
  const im = data.impact || data;
  const level = im.impact_level;
  return (
    <div className="space-y-4" data-testid="pc-impact">
      {/* Price header */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-stone-50 px-4 py-3">
        <div>
          <p className="text-xs text-ink-mute">{t("Product")}</p>
          <p className="font-heading text-base font-bold text-ink" data-testid="pc-impact-product">{data.product_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <p className="text-xs text-ink-mute">{t("Current Price")}</p>
            <p className="text-sm font-semibold text-ink">{rp(data.current_price)}</p>
          </div>
          <ArrowRight size={16} className="text-ink-mute" />
          <div>
            <p className="text-xs text-ink-mute">{t("New Price")}</p>
            <p className="text-sm font-bold text-primary" data-testid="pc-impact-newprice">{rp(data.new_price)}</p>
          </div>
        </div>
        <div className="ml-auto text-right">
          <p className="text-xs text-ink-mute">{t("Difference")}</p>
          <p className={`text-sm font-bold ${im.difference >= 0 ? "text-danger-text" : "text-success-text"}`}>
            {im.difference >= 0 ? "+" : ""}{rp(im.difference)}
          </p>
        </div>
        <Badge tone={LEVEL_TONE[level]} data-testid="pc-impact-level">{pctStr(im.percentage_change)} · {t(level)}</Badge>
      </div>

      {/* High / Critical warning */}
      {level !== "NORMAL" && (
        <div className={`flex items-start gap-2.5 rounded-xl px-4 py-3 ${level === "CRITICAL" ? "bg-danger-light" : "bg-warn-light"}`} data-testid="pc-impact-warning">
          <AlertTriangle size={18} className={`mt-0.5 shrink-0 ${level === "CRITICAL" ? "text-danger-text" : "text-warn-text"}`} />
          <p className={`text-xs ${level === "CRITICAL" ? "text-danger-text" : "text-warn-text"}`}>
            {level === "CRITICAL"
              ? t("Critical price change (over 50%). Review carefully before approving.")
              : t("High impact price change (over 20%). Please review the affected products.")}
          </p>
        </div>
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={t("Affected Products")} value={im.affected_products_count} testid="pc-stat-products" />
        <Stat label={t("Affected Recipes")} value={im.affected_recipes_count} testid="pc-stat-recipes" />
        <Stat label={t("Est. HPP Impact")} value={pctStr(im.estimated_hpp_impact_pct)} testid="pc-stat-hpp" tone="text-warn-text" />
        <Stat label={t("Below Target After")} value={im.below_target_count} testid="pc-stat-belowtarget" tone={im.below_target_count > 0 ? "text-danger-text" : "text-ink"} />
      </div>

      {/* Affected products list */}
      {im.affected_products?.length > 0 ? (
        <div className="overflow-hidden rounded-2xl border border-line" data-testid="pc-affected-list">
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-stone-100 text-left text-xs uppercase tracking-wide text-ink-mute">
                <tr>
                  <th className="px-3 py-2">{t("Product")}</th>
                  <th className="px-3 py-2 text-right">{t("Cost")}</th>
                  <th className="px-3 py-2 text-right">HPP</th>
                  <th className="px-3 py-2 text-right">{t("Margin")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {im.affected_products.map((a, i) => (
                  <tr key={i} className={a.below_target ? "bg-danger-light/40" : ""}>
                    <td className="px-3 py-2">
                      <span className="font-medium text-ink">{a.product_name}</span>
                      <Badge tone="mute" className="ml-2 !px-1.5 !py-0.5 !text-[10px]">{a.type === "MENU" ? t("Menu") : t("Production")}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right text-ink-soft">{rp(a.current_cost)} → <span className="font-semibold text-ink">{rp(a.new_cost)}</span></td>
                    <td className="px-3 py-2 text-right font-semibold text-warn-text">{pctStr(a.hpp_change_pct)}</td>
                    <td className="px-3 py-2 text-right">
                      {a.new_margin_pct == null ? <span className="text-ink-mute">—</span> : (
                        <span className={a.below_target ? "font-bold text-danger-text" : "text-ink"}>
                          {a.current_margin_pct}% → {a.new_margin_pct}%
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="rounded-xl bg-stone-50 px-3 py-3 text-center text-xs text-ink-mute">{t("No products use this material in any recipe.")}</p>
      )}
    </div>
  );
}

const Stat = ({ label, value, tone, testid }) => (
  <div className="rounded-xl border border-line bg-white px-3 py-2.5" data-testid={testid}>
    <p className={`font-heading text-lg font-bold ${tone || "text-ink"}`}>{value ?? "—"}</p>
    <p className="text-[11px] uppercase tracking-wide text-ink-mute">{label}</p>
  </div>
);

// ---------------- Request form (AI text + manual) ----------------
function RequestForm({ onCreated }) {
  const { t } = useT();
  const toast = useToast();
  const [mode, setMode] = useState("ai");
  const [text, setText] = useState("");
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [parsing, setParsing] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [extracted, setExtracted] = useState(null);
  const [preview, setPreview] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/price-changes/products").then(({ data }) => setProducts(data.products || [])).catch(() => {});
  }, []);

  const reset = () => { setCandidates([]); setExtracted(null); setPreview(null); };

  const computeNewPrice = (cand) => {
    if (!extracted || extracted.amount == null) return null;
    const cur = cand.current_price;
    if (extracted.change_type === "ABSOLUTE") return extracted.amount;
    if (extracted.change_type === "INCREASE") return cur + extracted.amount;
    return Math.max(0, cur - extracted.amount);
  };

  const previewFor = useCallback(async (pid, price, productName, currentPrice) => {
    try {
      const { data } = await api.post("/price-changes/preview", { product_id: pid, new_price: Number(price) });
      setPreview({ product_id: pid, product_name: data.product_name, unit: data.unit, current_price: data.current_price, new_price: data.new_price, impact: data.impact });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Something went wrong"));
    }
  }, [t, toast]);

  const onAnalyze = async () => {
    if (!text.trim()) return;
    setParsing(true); reset();
    try {
      const { data } = await api.post("/price-changes/parse", { text });
      setExtracted(data.extracted);
      if (!data.found) { toast.error(data.message); setParsing(false); return; }
      if (data.ambiguous) { setCandidates(data.candidates); setParsing(false); return; }
      if (data.needs_price) {
        const c = data.candidates[0];
        setProductId(c.product_id); setNewPrice(String(c.current_price));
        toast.info(data.message);
        setParsing(false); return;
      }
      if (data.proposed) {
        setPreview({ ...data.proposed, impact: data.impact });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Something went wrong"));
    } finally { setParsing(false); }
  };

  const pickCandidate = async (c) => {
    const np = computeNewPrice(c);
    if (np == null) { setProductId(c.product_id); setNewPrice(String(c.current_price)); setCandidates([]); return; }
    setCandidates([]);
    await previewFor(c.product_id, np, c.product_name, c.current_price);
  };

  const onManualPreview = async () => {
    if (!productId || !newPrice) { toast.error(t("Select a product and enter a new price")); return; }
    await previewFor(productId, Number(newPrice));
  };

  const onCreate = async () => {
    if (!preview) return;
    setCreating(true);
    try {
      await api.post("/price-changes", {
        product_id: preview.product_id, new_price: preview.new_price,
        source: mode === "ai" ? "ai_chat" : "manual", raw_text: mode === "ai" ? text : null,
      });
      toast.success(t("Price change request created (pending approval)"));
      setText(""); setProductId(""); setNewPrice(""); reset();
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Something went wrong"));
    } finally { setCreating(false); }
  };

  return (
    <Card data-testid="pc-form">
      <div className="mb-4 inline-flex rounded-xl border border-line bg-white p-1">
        {[{ id: "ai", label: t("AI / Text"), icon: Sparkles }, { id: "manual", label: t("Manual"), icon: PencilLine }].map((m) => (
          <button key={m.id} data-testid={`pc-mode-${m.id}`} onClick={() => { setMode(m.id); reset(); }}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${mode === m.id ? "bg-primary text-white shadow-sm" : "text-ink-soft hover:bg-stone-100"}`}>
            <m.icon size={15} /> {m.label}
          </button>
        ))}
      </div>

      {mode === "ai" ? (
        <div className="space-y-3">
          <textarea data-testid="pc-ai-input" value={text} onChange={(e) => setText(e.target.value)} rows={2}
            placeholder={t("e.g. Harga cabe merah naik 20 ribu per kg")} className="cf-input resize-none" />
          <Button onClick={onAnalyze} loading={parsing} disabled={!text.trim()} data-testid="pc-analyze-btn">
            <Sparkles size={16} /> {t("Analyze")}
          </Button>
          {candidates.length > 0 && (
            <div className="rounded-xl border border-warn/40 bg-warn-light/50 p-3" data-testid="pc-candidates">
              <p className="mb-2 text-xs font-semibold text-warn-text">{t("Multiple products match. Choose one:")}</p>
              <div className="flex flex-wrap gap-2">
                {candidates.map((c) => (
                  <button key={c.product_id} data-testid={`pc-candidate-${c.product_id}`} onClick={() => pickCandidate(c)}
                    className="rounded-full border border-line bg-white px-3 py-1.5 text-xs font-medium text-ink-soft hover:border-primary hover:text-primary">
                    {c.product_name} · {rp(c.current_price)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-[1fr,200px,auto] sm:items-end">
          <Select label={t("Raw Material")} value={productId} onChange={(e) => setProductId(e.target.value)} data-testid="pc-manual-product">
            <option value="">{t("Select product")}</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({rp(p.price)}/{p.unit})</option>)}
          </Select>
          <Input label={t("New Price")} type="number" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} placeholder="0" data-testid="pc-manual-price" />
          <Button variant="outline" onClick={onManualPreview} data-testid="pc-manual-preview-btn">{t("Preview")}</Button>
        </div>
      )}

      {preview && (
        <div className="mt-5 border-t border-line pt-5">
          <ImpactPreview data={preview} />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={reset} data-testid="pc-cancel-btn">{t("Cancel")}</Button>
            <Button onClick={onCreate} loading={creating} data-testid="pc-create-btn">
              <FileText size={16} /> {t("Create Draft (Pending Approval)")}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------- Detail modal ----------------
function DetailModal({ id, onClose, onChanged, canApprove }) {
  const { t } = useT();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/price-changes/${id}`).then(({ data }) => setData(data)).catch(() => toast.error(t("Failed to load")));
  }, [id, t, toast]);

  const decide = async (action) => {
    setBusy(true);
    try {
      await api.post(`/price-changes/${id}/${action}`, { notes: null });
      toast.success(action === "approve" ? t("Approved — Product Master price updated") : t("Request rejected"));
      onChanged?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Something went wrong"));
    } finally { setBusy(false); }
  };

  const pending = data?.status === "PENDING_APPROVAL";
  return (
    <Modal open={!!id} onClose={onClose} size="lg" title={data ? `${data.reference_no} · ${data.product_name}` : t("Price Change")}
      footer={data && pending && canApprove ? (
        <>
          <Button variant="danger" onClick={() => decide("reject")} loading={busy} data-testid="pc-reject-btn"><X size={16} /> {t("Reject")}</Button>
          <Button variant="success" onClick={() => decide("approve")} loading={busy} data-testid="pc-approve-btn"><Check size={16} /> {t("Approve")}</Button>
        </>
      ) : <Button variant="outline" onClick={onClose}>{t("Close")}</Button>}>
      {!data ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-ink-soft">
            <Badge tone={STATUS_TONE[data.status]} data-testid="pc-detail-status">{t(STATUS_LABEL[data.status])}</Badge>
            <span>· {t("Requested by")} <b className="text-ink">{data.requested_by_name}</b></span>
            <span>· {t("Source")}: {data.source}</span>
            {data.approved_by_name && <span>· {data.status === "APPROVED" ? t("Approved by") : t("Decided by")} <b className="text-ink">{data.approved_by_name}</b></span>}
          </div>
          {data.raw_text && <p className="rounded-lg bg-stone-50 px-3 py-2 text-xs italic text-ink-soft">"{data.raw_text}"</p>}
          <ImpactPreview data={{ product_name: data.product_name, current_price: data.current_product_price ?? data.old_price, new_price: data.new_price, impact: data.impact }} />
        </div>
      )}
    </Modal>
  );
}

// ---------------- Requests list ----------------
function RequestsList({ refreshKey, onOpen }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (status) params.status = status;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    const { data } = await api.get("/price-changes", { params });
    setData(data);
  }, [status, from, to]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const c = data?.counts || {};
  const tabs = [
    { id: "", label: t("All") },
    { id: "PENDING_APPROVAL", label: `${t("Pending")} (${c.pending ?? 0})` },
    { id: "APPROVED", label: `${t("Approved")} (${c.approved ?? 0})` },
    { id: "REJECTED", label: `${t("Rejected")} (${c.rejected ?? 0})` },
  ];

  return (
    <Card data-testid="pc-list">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex flex-wrap rounded-xl border border-line bg-white p-1">
          {tabs.map((tb) => (
            <button key={tb.id} data-testid={`pc-filter-${tb.id || "all"}`} onClick={() => setStatus(tb.id)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${status === tb.id ? "bg-primary text-white" : "text-ink-soft hover:bg-stone-100"}`}>
              {tb.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Filter size={15} className="text-ink-mute" />
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="cf-input !py-1.5 !text-xs" data-testid="pc-filter-from" />
          <span className="text-ink-mute">–</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="cf-input !py-1.5 !text-xs" data-testid="pc-filter-to" />
        </div>
      </div>

      {!data ? <div className="flex h-32 items-center justify-center"><Spinner className="h-7 w-7" /></div>
        : data.rows.length === 0 ? <EmptyState title={t("No price change requests")} subtitle={t("Create one above using AI text or the manual form.")} />
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pc-table">
              <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-mute">
                <tr>
                  <th className="py-2 pr-3">{t("Reference")}</th>
                  <th className="py-2 pr-3">{t("Product")}</th>
                  <th className="py-2 pr-3 text-right">{t("Old")} → {t("New")}</th>
                  <th className="py-2 pr-3 text-right">{t("Change")}</th>
                  <th className="py-2 pr-3">{t("Status")}</th>
                  <th className="py-2 pr-3">{t("Requested by")}</th>
                  <th className="py-2 pr-3">{t("Date")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data.rows.map((r) => (
                  <tr key={r.id} className="cursor-pointer hover:bg-stone-50" onClick={() => onOpen(r.id)} data-testid={`pc-row-${r.reference_no}`}>
                    <td className="py-2.5 pr-3 font-mono text-xs font-semibold text-primary">{r.reference_no}</td>
                    <td className="py-2.5 pr-3 font-medium text-ink">{r.product_name}</td>
                    <td className="py-2.5 pr-3 text-right text-ink-soft">{rp(r.old_price)} → <b className="text-ink">{rp(r.new_price)}</b></td>
                    <td className="py-2.5 pr-3 text-right">
                      <Badge tone={LEVEL_TONE[r.impact_level]}>{pctStr(r.percentage_change)}</Badge>
                    </td>
                    <td className="py-2.5 pr-3"><Badge tone={STATUS_TONE[r.status]}>{t(STATUS_LABEL[r.status])}</Badge></td>
                    <td className="py-2.5 pr-3 text-xs text-ink-soft">{r.requested_by_name}</td>
                    <td className="py-2.5 pr-3 text-xs text-ink-mute">{(r.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </Card>
  );
}

// ---------------- Page ----------------
export default function PriceChanges() {
  const { t } = useT();
  const { hasPermission } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [openId, setOpenId] = useState(null);
  const canApprove = hasPermission("approve_price_change");
  const bump = () => setRefreshKey((k) => k + 1);

  return (
    <div data-testid="price-changes-page">
      <PageHeader title={t("Raw Material Price Changes")} subtitle={t("AI-assisted, approval-controlled Product Master price updates")} icon={TrendingUp} />

      <div className="mb-4 flex items-start gap-2.5 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="pc-safety-banner">
        <ShieldCheck size={18} className="mt-0.5 shrink-0 text-blue-600" />
        <p className="text-xs text-blue-800">{t("AI prepares price-change drafts only. Prices update ONLY after an Owner or Super Admin approves. Selling prices, transfer prices and historical HPP are never auto-changed.")}</p>
      </div>

      <div className="space-y-5">
        <RequestForm onCreated={bump} />
        <RequestsList refreshKey={refreshKey} onOpen={setOpenId} />
      </div>

      <DetailModal id={openId} onClose={() => setOpenId(null)} onChanged={bump} canApprove={canApprove} />
    </div>
  );
}
